import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Loader2, Save, ArrowLeft, ChevronsUpDown, Check, Search, Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { sheltersApi } from '../api';
import type { Shelter } from '../types';
import { citiesApi } from '@/features/cities/api';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

// Helpers that allow empty string as valid input for optional fields
const optStr = (max?: number) => (max ? z.string().max(max) : z.string()).or(z.literal('')).optional();
const optUrl = () => z.string().url('URL inválida').max(255).or(z.literal('')).optional();

const schema = z.object({
  name: z.string().min(1, 'Name is required').max(120),
  slug: optStr(120),
  contactEmail: z.string().email('Invalid email').max(255).or(z.literal('')).optional(),
  contactPhone: optStr(),
  address: optStr(),
  websiteUrl: optUrl(),
  instagram: optStr(255),
  facebook: optStr(255),
  twitter: optStr(255),
  tiktok: optStr(255),
  whatsapp: optStr(32),
  lat: z.coerce.number().optional(),
  lon: z.coerce.number().optional(),
  cityId: z.coerce.number().optional(),
});

type ShelterFormData = z.infer<typeof schema>;

export default function ShelterFormPage() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { register, control, handleSubmit, formState: { errors }, reset, setValue, watch, getValues } = useForm<ShelterFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      contactEmail: '',
      contactPhone: '',
      address: '',
      websiteUrl: '',
      slug: '',
      instagram: '',
      facebook: '',
      twitter: '',
      tiktok: '',
      whatsapp: '',
    },
  });

  const { data: existing } = useQuery({
    queryKey: ['shelter', id],
    queryFn: () => sheltersApi.getById(Number(id)),
    enabled: isEdit,
  });

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [bannerFile, setBannerFile] = useState<File | null>(null);

  useEffect(() => {
    if (existing) {
      const mapNullable = (v: any) => (v == null ? '' : v);
      const mapped: any = {
        ...existing,
        slug: mapNullable((existing as any).slug),
        contactEmail: (existing as any).contactEmail ?? undefined,
        contactPhone: mapNullable((existing as any).contactPhone),
        address: mapNullable((existing as any).address),
        websiteUrl: (existing as any).websiteUrl ?? undefined,
        instagram: mapNullable((existing as any).instagram),
        facebook: mapNullable((existing as any).facebook),
        twitter: mapNullable((existing as any).twitter),
        tiktok: mapNullable((existing as any).tiktok),
        whatsapp: mapNullable((existing as any).whatsapp),
      };
      reset(mapped);
      setLogoFile(null);
      setBannerFile(null);
      if ((existing as any).city?.id && !(existing as any).cityId) {
        setValue('cityId', (existing as any).city.id);
      }
    }
  }, [existing, reset, setValue]);

  // Cities datasource (load many, filter client-side case-insensitively)
  const [cityQuery, setCityQuery] = useState('');
  const [cityOpen, setCityOpen] = useState(false);
  const { data: citiesAll } = useQuery({
    queryKey: ['cities-all'],
    queryFn: () => citiesApi.listFirst(),
  });
  const selectedCityId = watch('cityId');
  const flatCities: any[] | undefined = Array.isArray(citiesAll) ? (citiesAll as any[]) : (citiesAll as any)?.data;
  const filteredCities = useMemo(() => {
    if (!flatCities) return [] as any[];
    const q = cityQuery.trim().toLowerCase();
    if (!q) return flatCities.slice(0, 100);
    return flatCities.filter((c: any) =>
      String(c.name || '').toLowerCase().includes(q) || String(c.department || '').toLowerCase().includes(q),
    ).slice(0, 100);
  }, [flatCities, cityQuery]);
  const selectedCityLabel = useMemo(() => {
    const flat = flatCities;
    const found = flat?.find((c: any) => Number(c.id) === Number(selectedCityId));
    if (found) return `${found.name}${found.department ? ` - ${found.department}` : ''}`;
    const exCity = (existing as any)?.city;
    if (exCity) return `${exCity.name}${exCity.department ? ` - ${exCity.department}` : ''}`;
    return undefined;
  }, [flatCities, selectedCityId, existing]);

  const mutation = useMutation({
    mutationFn: (payload: Shelter) => isEdit ? sheltersApi.update(Number(id), payload) : sheltersApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shelters'] });
      toast({ title: isEdit ? 'Shelter updated' : 'Shelter created' });
      navigate('/shelters');
    },
    onError: (error: any) => {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error?.response?.data?.error?.message || 'Failed to save shelter',
      });
    },
  });

  const onSubmit = (data: ShelterFormData) => {
    // Normalize phone to digits-only before submit
    const normalized: any = { ...data };
    if (normalized.contactPhone !== undefined) {
      const digits = String(normalized.contactPhone || '').replace(/\D/g, '');
      if (digits.length > 0) normalized.contactPhone = digits; else normalized.contactPhone = '';
    }
    if (normalized.whatsapp !== undefined) {
      const digits = String(normalized.whatsapp || '').replace(/\D/g, '');
      normalized.whatsapp = digits || undefined;
    }
    // Convert empty strings to undefined for optional text/url fields
    ['slug','contactEmail','address','websiteUrl','instagram','facebook','twitter','tiktok'].forEach((k) => {
      if (normalized[k] === '') normalized[k] = undefined;
    });
    const hasFiles = !!logoFile || !!bannerFile;
    if (!hasFiles) {
      mutation.mutate(normalized as Shelter);
      return;
    }
    const fd = new FormData();
    Object.entries(normalized).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      fd.append(key, String(value));
    });
    if (logoFile) fd.append('logotipo', logoFile);
    if (bannerFile) fd.append('banner', bannerFile);
    mutation.mutate(fd as FormData);
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="sm" onClick={() => navigate('/shelters')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-4xl font-bold">{isEdit ? 'Editar Entidad' : 'Nueva Entidad'}</h1>
            <p className="text-muted-foreground">
              {isEdit ? 'Actualiza la información de la entidad' : 'Registra una nueva entidad'}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Card className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="name">Nombre *</Label>
                <Input id="name" {...register('name')} placeholder="Nombre de la entidad" />
                {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="slug">Slug</Label>
                <Input id="slug" {...register('slug')} placeholder="shelter-ejemplo" />
                {errors.slug && <p className="text-sm text-destructive">{errors.slug.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="logotipo">Logotipo</Label>
                <Input
                  id="logotipo"
                  name="logotipo"
                  type="file"
                  accept="image/*"
                  onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                />
                <p className="text-xs text-muted-foreground">Dimensión recomendada 300×300</p>
                {(existing as any)?.logotipo && !logoFile && (
                  <p className="text-xs text-muted-foreground">Actual: {(existing as any).logotipo}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="banner">Banner</Label>
                <Input
                  id="banner"
                  name="banner"
                  type="file"
                  accept="image/*"
                  onChange={(e) => setBannerFile(e.target.files?.[0] || null)}
                />
                <p className="text-xs text-muted-foreground">Dimensión remomendada 2000x1125</p>
                {(existing as any)?.banner && !bannerFile && (
                  <p className="text-xs text-muted-foreground">Actual: {(existing as any).banner}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="contactEmail">Email</Label>
                <Input id="contactEmail" type="email" placeholder="contacto@entidad.org" {...register('contactEmail')} />
                {errors.contactEmail && <p className="text-sm text-destructive">{errors.contactEmail.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="contactPhone">Teléfono</Label>
                <Controller
                  name="contactPhone"
                  control={control}
                  render={({ field }) => {
                    const formatPhone = (v: string) => {
                      const digits = v.replace(/\D/g, '').slice(0, 10);
                      const p1 = digits.slice(0, 3);
                      const p2 = digits.slice(3, 6);
                      const p3 = digits.slice(6, 10);
                      return [p1, p2, p3].filter(Boolean).join(' ');
                    };
                    return (
                      <Input
                        id="contactPhone"
                        inputMode="numeric"
                        placeholder="000 000 0000"
                        value={field.value || ''}
                        onChange={(e) => field.onChange(formatPhone(e.target.value))}
                      />
                    );
                  }}
                />
                {errors.contactPhone && <p className="text-sm text-destructive">{errors.contactPhone.message}</p>}
              </div>

              {/* City selector above address */}
              <div className="space-y-2 md:col-span-2">
                <Label>Ciudad</Label>
                <Popover open={cityOpen} onOpenChange={setCityOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" aria-expanded={cityOpen} className="w-full justify-between">
                      {selectedCityLabel || 'Selecciona una ciudad'}
                      <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
                    <Command>
                      <CommandInput placeholder="Buscar ciudad..." value={cityQuery} onValueChange={setCityQuery} />
                      <CommandList>
                        <CommandEmpty>No results found.</CommandEmpty>
                        <CommandGroup>
                          {filteredCities.map((c: any) => (
                            <CommandItem
                              key={c.id}
                              value={`${c.name}${c.department ? ` - ${c.department}` : ''}`}
                              onSelect={() => {
                                setValue('cityId', c.id, { shouldDirty: true });
                                setCityOpen(false);
                              }}
                            >
                              <Check className={"mr-2 h-4 w-4 " + (selectedCityId === c.id ? 'opacity-100' : 'opacity-0')} />
                              {c.name}
                              {c.department ? <span className="text-muted-foreground ml-2">- {c.department}</span> : null}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {errors.cityId && <p className="text-sm text-destructive">{errors.cityId.message}</p>}
                {/* ensure RHF registers cityId so it is included on submit */}
                <input type="hidden" {...register('cityId')} />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="address">Dirección</Label>
                <Input id="address" placeholder="Calle 123" {...register('address')} />
              </div>
              {/* Redes sociales y web */}
              <div className="md:col-span-2">
                <h3 className="text-lg font-semibold mb-2">Redes sociales</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="websiteUrl">Sitio web</Label>
                    <Input id="websiteUrl" placeholder="https://www.entidad.org" {...register('websiteUrl')} />
                    {errors.websiteUrl && <p className="text-sm text-destructive">{errors.websiteUrl.message}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="instagram">Instagram</Label>
                    <Input id="instagram" placeholder="https://instagram.com/tu_refugio" {...register('instagram')} />
                    {errors.instagram && <p className="text-sm text-destructive">{errors.instagram.message}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="facebook">Facebook</Label>
                    <Input id="facebook" placeholder="https://facebook.com/tu_refugio" {...register('facebook')} />
                    {errors.facebook && <p className="text-sm text-destructive">{errors.facebook.message}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="twitter">X(Twitter)</Label>
                    <Input id="twitter" placeholder="https://x.com/tu_refugio" {...register('twitter')} />
                    {errors.twitter && <p className="text-sm text-destructive">{errors.twitter.message}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="tiktok">TikTok</Label>
                    <Input id="tiktok" placeholder="https://tiktok.com/@tu_refugio" {...register('tiktok')} />
                    {errors.tiktok && <p className="text-sm text-destructive">{errors.tiktok.message}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="whatsapp">WhatsApp</Label>
                    <Input id="whatsapp" placeholder="5491100000000" {...register('whatsapp')} />
                    {errors.whatsapp && <p className="text-sm text-destructive">{errors.whatsapp.message}</p>}
                  </div>
                </div>
              </div>
            </div>
          </Card>

          <div className="flex justify-end gap-4 mt-6">
            <Button type="button" variant="outline" onClick={() => navigate('/shelters')}>
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Guardar
                </>
              )}
            </Button>
          </div>
        </form>

        {/* Usuarios vinculados al entidad */}
        {isEdit && (
          <ShelterUsersSection shelterId={Number(id)} />
        )}
      </div>
    </Layout>
  );
}

// --- Usuarios vinculados al entidad ---
import { usersApi } from '@/features/users/api';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAuth } from '@/hooks/useAuth';

function ShelterUsersSection({ shelterId }: { shelterId: number }) {
  const { isAdminWithShelter } = useAuth();
  const queryClient = useQueryClient();
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sort, setSort] = useState<'name'|'email'|'role'|'createdAt'>('name');
  const [dir, setDir] = useState<'asc'|'desc'>('asc');
  const [roleForNew, setRoleForNew] = useState('');
  const [selectedUser, setSelectedUser] = useState<{ id: number; label: string } | null>(null);
  const [isReplaceDialogOpen, setIsReplaceDialogOpen] = useState(false);
  const [replaceCandidate, setReplaceCandidate] = useState<{ userId: number; roleInShelter?: string } | null>(null);
  const [isUnlinkDialogOpen, setIsUnlinkDialogOpen] = useState(false);
  const [unlinkTarget, setUnlinkTarget] = useState<number | null>(null);
  const [isUnlinking, setIsUnlinking] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 400);
    return () => clearTimeout(t);
  }, [q]);

  const { data, isLoading } = useQuery({
    queryKey: ['shelter-users', shelterId, debounced, page, pageSize, sort, dir],
    queryFn: () => usersApi.listShelterUsers(shelterId, { q: debounced || undefined, page, pageSize, sort, dir }),
  });

  const rows: any[] = (data as any)?.data || [];
  const total: number = (data as any)?.total ?? rows.length;

  const unlinkMutation = useMutation({
    mutationFn: ({ userId }: { userId: number }) => usersApi.unlinkUserFromShelter(userId, shelterId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shelter-users'] });
      queryClient.invalidateQueries({ queryKey: ['shelter-candidates'] });
      toast({ title: 'Vínculo eliminado' });
    },
    onError: (e: any) => toast({ variant: 'destructive', title: 'Error', description: e?.response?.data?.error?.message || 'Failed to unlink' }),
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ userId, roleInShelter }: { userId: number; roleInShelter?: string }) => usersApi.updateUserShelterRole(userId, shelterId, roleInShelter),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shelter-users'] });
      toast({ title: 'Función actualizada' });
    },
    onError: (e: any) => toast({ variant: 'destructive', title: 'Error', description: e?.response?.data?.error?.message || 'Failed to update role' }),
  });

  const addLink = async () => {
    if (!selectedUser) return;
    try {
      await usersApi.linkUserToShelter(selectedUser.id, { shelterId, roleInShelter: roleForNew || undefined });
      setSelectedUser(null);
      setRoleForNew('');
      queryClient.invalidateQueries({ queryKey: ['shelter-users'] });
      queryClient.invalidateQueries({ queryKey: ['shelter-candidates'] });
      toast({ title: 'Usuario vinculado' });
    } catch (e: any) {
      const msg = e?.response?.data?.error?.message || '';
      toast({ variant: 'destructive', title: 'Error', description: msg || 'No se pudo vincular' });
      if (String(msg).toLowerCase().includes('replace')) {
        setReplaceCandidate({ userId: selectedUser.id, roleInShelter: roleForNew || undefined });
        setIsReplaceDialogOpen(true);
      }
    }
  };

  const confirmReplace = async () => {
    if (!replaceCandidate) return;
    try {
      await usersApi.putUserShelters(replaceCandidate.userId, [
        { shelterId, roleInShelter: replaceCandidate.roleInShelter },
      ]);
      setIsReplaceDialogOpen(false);
      setReplaceCandidate(null);
      setSelectedUser(null);
      setRoleForNew('');
      queryClient.invalidateQueries({ queryKey: ['shelter-users'] });
      queryClient.invalidateQueries({ queryKey: ['shelter-candidates'] });
      toast({ title: 'Vínculo sustituido' });
    } catch (e: any) {
      toast({ variant: 'destructive', title: 'Error', description: e?.response?.data?.error?.message || 'No se pudo sustituir el vínculo' });
    }
  };

  // User search for autocomplete
  const [userSearch, setUserSearch] = useState('');
  const [userDebounced, setUserDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setUserDebounced(userSearch), 300);
    return () => clearTimeout(t);
  }, [userSearch]);
  const { data: candidateRes } = useQuery({
    queryKey: ['shelter-candidates', shelterId, userDebounced],
    queryFn: () => usersApi.listShelterCandidates(shelterId, { q: userDebounced || undefined, page: 1, pageSize: 10, sort: 'name', dir: 'asc' }),
    enabled: !!userDebounced,
  });
  const options = ((candidateRes as any)?.data || []).map((u: any) => ({ id: u.userId, label: `${u.fullName} — ${u.email}` }));

  return (
    <Card className="mt-6 p-6 space-y-4">
      <h2 className="text-xl font-semibold">Usuarios de la entidad</h2>
      {!isAdminWithShelter && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 items-end">
          <div className="md:col-span-2">
            <Label>Buscar usuario</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="justify-between w-full">
                  {selectedUser ? selectedUser.label : 'Escribe para buscar...'}
                  <Search className="h-4 w-4 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0 w-[--radix-popover-trigger-width]">
                <Command>
                  <CommandInput placeholder="Nombre o email..." value={userSearch} onValueChange={setUserSearch} />
                  <CommandList>
                    <CommandEmpty>Sin resultados</CommandEmpty>
                    <CommandGroup>
                      {options.map((o: any) => (
                        <CommandItem key={o.id} value={o.label} onSelect={() => { setSelectedUser({ id: o.id, label: o.label }); }}>
                          {o.label}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
          <div className="flex-1">
            <Label>Función en la entidad</Label>
            <Input value={roleForNew} onChange={(e)=>setRoleForNew(e.target.value)} placeholder="voluntario, gestor, etc." />
          </div>
          <div className="flex items-end">
            <Button type="button" onClick={addLink} disabled={!selectedUser}>Agregar</Button>
          </div>
        </div>
      )}

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead
                onClick={() => setSort((prev) => {
                  const col: typeof prev = 'name';
                  setDir((d)=> (sort==='name' ? (d==='asc'?'desc':'asc') : 'asc'));
                  return col;
                })}
                className="cursor-pointer select-none"
              >
                Nombre {sort==='name' ? (dir==='asc'?<ArrowUp className="inline h-3 w-3 ml-1" />:<ArrowDown className="inline h-3 w-3 ml-1" />) : <ChevronsUpDown className="inline h-3 w-3 ml-1 opacity-50" />}
              </TableHead>
              <TableHead
                onClick={() => setSort((prev) => {
                  const col: typeof prev = 'email';
                  setDir((d)=> (sort==='email' ? (d==='asc'?'desc':'asc') : 'asc'));
                  return col;
                })}
                className="cursor-pointer select-none"
              >
                Email {sort==='email' ? (dir==='asc'?<ArrowUp className="inline h-3 w-3 ml-1" />:<ArrowDown className="inline h-3 w-3 ml-1" />) : <ChevronsUpDown className="inline h-3 w-3 ml-1 opacity-50" />}
              </TableHead>
              <TableHead
                onClick={() => setSort((prev) => {
                  const col: typeof prev = 'role';
                  setDir((d)=> (sort==='role' ? (d==='asc'?'desc':'asc') : 'asc'));
                  return col;
                })}
                className="cursor-pointer select-none"
              >
                Función {sort==='role' ? (dir==='asc'?<ArrowUp className="inline h-3 w-3 ml-1" />:<ArrowDown className="inline h-3 w-3 ml-1" />) : <ChevronsUpDown className="inline h-3 w-3 ml-1 opacity-50" />}
              </TableHead>
              <TableHead
                onClick={() => setSort((prev) => {
                  const col: typeof prev = 'createdAt';
                  setDir((d)=> (sort==='createdAt' ? (d==='asc'?'desc':'asc') : 'asc'));
                  return col;
                })}
                className="cursor-pointer select-none"
              >
                Vinculado {sort==='createdAt' ? (dir==='asc'?<ArrowUp className="inline h-3 w-3 ml-1" />:<ArrowDown className="inline h-3 w-3 ml-1" />) : <ChevronsUpDown className="inline h-3 w-3 ml-1 opacity-50" />}
              </TableHead>
              <TableHead>Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Cargando...</TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Sin usuarios vinculados</TableCell>
              </TableRow>
            ) : (
              rows.map((u: any) => (
                <TableRow key={u.userId}>
                  <TableCell className="font-medium">{u.fullName}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>
                    <Input
                      defaultValue={u.roleInShelter || ''}
                      onBlur={(e)=> updateRoleMutation.mutate({ userId: u.userId, roleInShelter: e.target.value })}
                      placeholder="rol en entidad"
                    />
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{u.linkedAt ? new Date(u.linkedAt).toLocaleString() : '-'}</TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:text-destructive"
                      onClick={() => { setUnlinkTarget(u.userId); setIsUnlinkDialogOpen(true); }}
                      aria-label="Eliminar"
                      title="Eliminar"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        <div className="p-4 border-t flex items-center justify-between">
          <p className="text-sm text-muted-foreground">Mostrando {rows.length} de {total} resultados</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={()=> setPage(page - 1)}>Anterior</Button>
            <Button variant="outline" size="sm" disabled={page * pageSize >= total} onClick={()=> setPage(page + 1)}>Siguiente</Button>
          </div>
        </div>
      </Card>

      {/* Reemplazar vínculo dialog */}
      <AlertDialog open={isReplaceDialogOpen} onOpenChange={setIsReplaceDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Usuario ya vinculado</AlertDialogTitle>
            <AlertDialogDescription>
              El usuario ya está vinculado a otro entidad. ¿Deseas sustituir el vínculo por este entidad?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmReplace}>Sustituir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmar eliminar vínculo */}
      <AlertDialog open={isUnlinkDialogOpen} onOpenChange={setIsUnlinkDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar vínculo del usuario?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. El usuario quedará sin este entidad asociado.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isUnlinking}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async () => {
                if (!unlinkTarget) return;
                try {
                  setIsUnlinking(true);
                  await unlinkMutation.mutateAsync({ userId: unlinkTarget });
                  setIsUnlinkDialogOpen(false);
                  setUnlinkTarget(null);
                } catch (e) {
                  // toast is handled in mutation onError
                } finally {
                  setIsUnlinking(false);
                }
              }}
            >
              {isUnlinking ? 'Eliminando...' : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
